import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type LexicalUpdateJSON,
  type NodeKey,
  type SerializedElementNode,
  type Spread,
} from 'lexical';

import {
  SLIDE_EDITOR_FONT_SIZES,
  type SlideEditorFontSize,
} from '@/styles/tokens/fonts';

import { InlineStyleElementNode } from '../inlineStyle';

export type SerializedFontSizeNode = Spread<
  { fontSize: number },
  SerializedElementNode
>;

export const ALLOWED_FONT_SIZES = Object.keys(SLIDE_EDITOR_FONT_SIZES).map(
  Number
) as unknown as readonly SlideEditorFontSize[];
export type AllowedFontSize = SlideEditorFontSize;
export const DEFAULT_FONT_SIZE: AllowedFontSize = 16;

/** Returns the rem CSS value for a given logical font size. */
export function fontSizeToCSS(size: number): string {
  const entry = SLIDE_EDITOR_FONT_SIZES[size as AllowedFontSize];
  return entry?.fontSize ?? `${size / 16}rem`;
}

/** Returns the unitless line-height for a given logical font size. */
export function lineHeightForSize(size: number): string {
  const entry = SLIDE_EDITOR_FONT_SIZES[size as AllowedFontSize];
  return entry?.lineHeight ?? '1.5';
}

export class FontSizeNode extends InlineStyleElementNode {
  __fontSize: number;

  static override getType(): string {
    return 'font-size';
  }

  static override clone(node: FontSizeNode): FontSizeNode {
    return new FontSizeNode(node.__fontSize, node.__key);
  }

  constructor(fontSize: number, key?: NodeKey) {
    super(key);
    this.__fontSize = fontSize;
  }

  override createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    span.style.fontSize = fontSizeToCSS(this.__fontSize);
    span.style.lineHeight = lineHeightForSize(this.__fontSize);
    span.dataset.lexicalFontSize = String(this.__fontSize);
    return span;
  }

  override updateDOM(
    prevNode: FontSizeNode,
    dom: HTMLElement,
    _config: EditorConfig
  ): boolean {
    if (prevNode.__fontSize !== this.__fontSize) {
      dom.style.fontSize = fontSizeToCSS(this.__fontSize);
      dom.style.lineHeight = lineHeightForSize(this.__fontSize);
      dom.dataset.lexicalFontSize = String(this.__fontSize);
    }
    return false;
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-font-size')) {
          return null;
        }
        return {
          conversion: convertFontSizeSpan,
          priority: 1,
        };
      },
    };
  }

  override exportDOM(
    _editor: import('lexical').LexicalEditor
  ): DOMExportOutput {
    const element = document.createElement('span');
    element.style.fontSize = fontSizeToCSS(this.__fontSize);
    element.style.lineHeight = lineHeightForSize(this.__fontSize);
    element.dataset.lexicalFontSize = String(this.__fontSize);
    return { element };
  }

  static override importJSON(
    serializedNode: SerializedFontSizeNode
  ): FontSizeNode {
    return $createFontSizeNode(serializedNode.fontSize).updateFromJSON(
      serializedNode
    );
  }

  override updateFromJSON(
    serializedNode: LexicalUpdateJSON<SerializedFontSizeNode>
  ): this {
    return super
      .updateFromJSON(serializedNode)
      .setFontSize(serializedNode.fontSize);
  }

  override exportJSON(): SerializedFontSizeNode {
    return {
      ...super.exportJSON(),
      fontSize: this.__fontSize,
      type: FontSizeNode.getType(),
      version: 1,
    };
  }

  getFontSize(): number {
    return this.getLatest().__fontSize;
  }

  setFontSize(fontSize: number): this {
    const self = this.getWritable();
    self.__fontSize = fontSize;
    return self;
  }
}

function convertFontSizeSpan(domNode: Node): DOMConversionOutput | null {
  if (!(domNode instanceof HTMLElement)) return null;
  const sizeAttr = domNode.getAttribute('data-lexical-font-size');
  if (!sizeAttr) return null;
  const size = parseInt(sizeAttr, 10);
  if (isNaN(size) || size <= 0) return null;
  return {
    node: $createFontSizeNode(size),
  };
}

export function $createFontSizeNode(fontSize: number): FontSizeNode {
  return $applyNodeReplacement(new FontSizeNode(fontSize));
}

export function $isFontSizeNode(
  node: LexicalNode | null | undefined
): node is FontSizeNode {
  return node instanceof FontSizeNode;
}
